import React from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, LayoutGrid, Link as LinkIcon } from 'lucide-react';
import { openGraphNode } from '../../graph/graphNavigation';
import { useOutline } from './useOutline';
import { useLinkedItems } from './useLinkedItems';
import OsScroll from '../ui/OsScroll';

const ICONS = { document: FileText, board: LayoutGrid };

function LinkedList({ documentId }) {
  const { t } = useTranslation();
  const { items, index } = useLinkedItems(documentId);
  if (!items.length) return null;
  return (
    <div className="doc-rail-linked" data-testid="doc-rail-linked">
      <div className="doc-rail-heading doc-rail-heading--linked">{t('docEditor.outline.linked')}</div>
      {items.map((node) => {
        const Icon = ICONS[node.type] || LinkIcon;
        const label = node.type === 'board' ? t('docEditor.outline.board', { name: node.label }) : node.label;
        return (
          <button key={node.id} type="button" className="doc-rail-link doc-rail-link--item" onClick={() => openGraphNode(node, index)}>
            <Icon size={12} strokeWidth={1.6} aria-hidden />
            <span>{label}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * "On this page" (card 2.4): the open document's h2/h3, h3 indented, the
 * section being read marked by the accent on the 1 px left border. A click
 * scrolls smoothly to the heading (its scroll-margin keeps it clear of the
 * toolbar). Below the outline, LINKED: the document's graph neighbours.
 *
 * `variant` "overlay" is the narrow-window form (card 2.6): the same rail
 * floating over the right edge, opened by the toolbar toggle.
 */
export default function OutlineRail({ documentId, bodyRef, subscribeBody, variant = 'inline' }) {
  const { t } = useTranslation();
  const { items, activeId } = useOutline({ documentId, bodyRef, subscribeBody, enabled: true });

  const go = (id) => {
    const el = bodyRef.current?.querySelector(`#${CSS.escape(id)}`);
    if (!el) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  };

  return (
    <aside className={`doc-rail doc-rail--${variant}`} aria-label={t('docEditor.outline.label')} data-testid="doc-outline-rail">
      {/* The app's scrollbar, not the native one (owner, 14.10.2026). */}
      <OsScroll className="doc-rail-scroll">
        <div className="doc-rail-inner">
          <div className="doc-rail-heading">{t('docEditor.outline.onThisPage')}</div>
          {/* null = not read yet: show nothing rather than flash the hint. */}
          {items === null ? null : items.length === 0 ? (
            <p className="doc-rail-empty">{t('docEditor.outline.empty')}</p>
          ) : (
            <nav className="doc-rail-toc">
              {items.map((it) => (
                <button
                  key={it.id}
                  type="button"
                  className={`doc-rail-link${it.level === 3 ? ' doc-rail-link--sub' : ''}${it.id === activeId ? ' is-active' : ''}`}
                  aria-current={it.id === activeId ? 'location' : undefined}
                  onClick={() => go(it.id)}
                >
                  {it.text}
                </button>
              ))}
            </nav>
          )}
          <LinkedList documentId={documentId} />
        </div>
      </OsScroll>
    </aside>
  );
}
